import {DateTime, DateTimeFormatOptions} from 'luxon';
import {omit, pick} from 'lodash-es';
import {filterNotNullish} from './array-utils';

export const dateFormatOptions: DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
};
export const timeFormatOptions: DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
};
export const dateTimeFormatOptions = {
    ...dateFormatOptions,
    ...timeFormatOptions,
};
export const dateTimeWithoutSecondsFormatOptions: DateTimeFormatOptions = {
    ...dateFormatOptions,
    ...omit(timeFormatOptions, 'second'),
};
/** A start/end date range. Shared so chart features in different apps agree on the shape. */
export type DateTimePeriod = {
    readonly startDate: DateTime;
    readonly endDate: DateTime;
};

/** Shift a period one calendar year into the past (e.g. for last-year comparison series). */
export function shiftPeriodOneYearBack(period: DateTimePeriod): DateTimePeriod {
    return {
        startDate: period.startDate.minus({years: 1}),
        endDate: period.endDate.minus({years: 1}),
    };
}

export function formatTime(value: DateTime, locale: string): string {
    return formatDateTime(value, locale, {
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatDate(
    value: DateTime,
    locale: string,
    dateFormat?: DateTimeFormatOptions,
    danishDateSeparator: '.' | '-' = '.',
): string {
    return formatDateTime(value, locale, dateFormat ?? dateFormatOptions, danishDateSeparator);
}

export function formatDateTime(
    dateTime: DateTime,
    locale: string,
    formatOptions?: Intl.DateTimeFormatOptions,
    danishDateSeparator: '.' | '-' = '.',
): string {
    const validatedLocale = getValidatedLocale(locale);
    const localeDateTime = dateTime.setLocale(validatedLocale);
    if (validatedLocale === 'da') {
        // Special handling of Danish: the time part is joined by colon (locale default is '.'),
        // and — only for all-numeric date formats — the date separator can be dashed per app config.
        const dateOptions = omit(formatOptions ?? dateTimeFormatOptions, timeOptionValues);
        const timeOptions = pick(formatOptions ?? dateTimeFormatOptions, timeOptionValues);
        const isNumericDate = dateOptions.month === 'numeric' || dateOptions.month === '2-digit';
        const rawDateString = toLocaleStringOrNull(localeDateTime, dateOptions);
        const dateString =
            danishDateSeparator === '-' && isNumericDate && rawDateString != null
                ? rawDateString.replace(/\./g, '-')
                : rawDateString;
        const timeString = toLocaleStringOrNull(localeDateTime, timeOptions)?.replace(/\./g, ':');
        return filterNotNullish([dateString, timeString]).join(' ');
    }
    return localeDateTime.toLocaleString(formatOptions ?? dateTimeFormatOptions);
}

export function getParseFormat(
    formatOptions: DateTimeFormatOptions,
    locale: string,
    danishDateSeparator: '.' | '-' = '.',
): string | null {
    const validatedLocale = getValidatedLocale(locale);
    const format = DateTime.parseFormatForOpts(formatOptions, {locale: validatedLocale});
    const isNumericDate = formatOptions.month === 'numeric' || formatOptions.month === '2-digit';
    if (
        format != null &&
        validatedLocale === 'da' &&
        danishDateSeparator === '-' &&
        isNumericDate
    ) {
        return format.replace(/\./g, '-');
    }
    return format;
}

/**
 * Expand a two-digit year into the 100-year window ending 30 years after `referenceDate`, so the
 * window follows today rather than sitting on a fixed cutoff. Luxon only offers the mutable global
 * `Settings.twoDigitCutoffYear`, which cannot express that. (AGI-19668)
 */
export function expandTwoDigitYear(twoDigitYear: number, referenceDate: DateTime): number {
    const latestYear = referenceDate.year + 30;
    const candidate = latestYear - (latestYear % 100) + twoDigitYear;
    return candidate > latestYear ? candidate - 100 : candidate;
}

function getValidatedLocale(locale: string): string {
    // Avoid American style dates when locale is English
    return locale === 'en' ? 'en-GB' : locale;
}

const timeOptionValues = [
    'fractionalSecondDigits',
    'hour',
    'hour12',
    'hourCycle',
    'minute',
    'second',
    'timeStyle',
    'timeZone',
    'timeZoneName',
];
function toLocaleStringOrNull(dateTime: DateTime, options: DateTimeFormatOptions): string | null {
    return Object.keys(options).length > 0 ? dateTime.toLocaleString(options) : null;
}
